import { ChangeEvent, useEffect, useState } from "react";
import { Autocomplete, FormControl } from "@mui/material";
import { useAppSelector } from "@/store/hooks";
import { useDebounce, useTranslate } from "@/hooks";
import { selectGptList } from "@/store/qas/qasSlice";
import { selectPromptList } from "@/store/qas/qasPromptSlice";
import { ANSWER_CACHE_USAGE_LIST } from "@/constants/questionConfigLists";
import { colorPrimary } from "@/constants/colors";
import { IActionInternalAsk, IActionInternalChat } from "@/types/ses/sesItemConfigTypes";
import { CachingType } from "@/types/qas/qasTypes";
import { IResponsePromptData } from "@/types/qas/qasPromptTypes";
import { Hint } from "@/HOC";
import { NoOptionText } from "@/ui";
import { CustomMenuItem, CustomTextField } from "@/components/MUI";
import RequestTemplatePopupInfo from "@/components/RequestTemplatePopupInfo/RequestTemplatePopupInfo";
import { ISaveHandlerProps, ISayInternalProps } from "./SayInternal.props";
import styles from "./SayInternal.module.scss";

const SayInternal = ({ action, idx, channel, isDisabled, selectService, handleSaveActionInStore }: ISayInternalProps): JSX.Element => {
	const [inputModelGPT, setInputModelGPT] = useState<string | null>(action.model); // модель gpt
	const [inputDepth, setInputDepth] = useState<number>(action.service === "qas-chat" ? action.depth : 3); // глубина истории сообщений
	const [selectCaching, setSelectCaching] = useState<CachingType>(action.service === "qas-ask" ? action.useCache : "checked"); // кеширование
	const [inputSelectedPrompt, setInputSelectedPrompt] = useState<IResponsePromptData | null>(null); // шаблон промпта

	const modelGptList = useAppSelector(selectGptList); // store - список моделей gpt
	const promptList = useAppSelector(selectPromptList); // store - список промптов

	const translate = useTranslate(); // hook для перевода текста
	const debouncedInputDepth = useDebounce(inputDepth, 1000); // задержка обработки глубины

	useEffect(() => {
		// шаблон промпта
		if (action.template) {
			const foundPrompt = promptList.data?.find((promptItem) => promptItem.id === action.template);
			foundPrompt && setInputSelectedPrompt(foundPrompt);
		}
	}, []);

	// следим за глубиной
	useEffect(() => {
		if (action.service === "qas-chat" && debouncedInputDepth !== action.depth) {
			sayInternalActionDataChangeHandler({ depth: debouncedInputDepth });
		}
	}, [debouncedInputDepth]);

	// обработчик изменения модели генерации
	const handleModelGPTChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null): void => {
		const formattedValue = value || "";
		setInputModelGPT(formattedValue);
		action.model !== formattedValue && sayInternalActionDataChangeHandler({ modelGpt: formattedValue });
	};

	// обработчик изменения использования кэша
	const handleCachingChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const value = e.target.value as CachingType;
		setSelectCaching(value);
		action.service === "qas-ask" && action.useCache !== value && sayInternalActionDataChangeHandler({ caching: value });
	};

	// обработчик изменения шаблона промпта
	const handleSelectedPromptChange = (_e: React.SyntheticEvent<Element, Event>, value: IResponsePromptData | null): void => {
		const formattedValue = value?.id || "";
		setInputSelectedPrompt(value);
		action.template !== formattedValue && sayInternalActionDataChangeHandler({ promptId: formattedValue });
	};

	// обработчик изменения данных действия "Спросить llm" / "Искать по документам в qas" (say-internal)
	const sayInternalActionDataChangeHandler = ({ modelGpt, depth, caching, promptId }: Partial<ISaveHandlerProps>): void => {
		const data: { channel: string; idx: number; action: Partial<IActionInternalChat | IActionInternalAsk> } = {
			channel,
			idx,
			action: {
				model: modelGpt ?? inputModelGPT ?? "",
				depth: selectService === "qas-chat" ? depth ?? inputDepth : undefined,
				useCache: selectService === "qas-ask" ? caching ?? selectCaching : undefined,
				template: promptId ?? inputSelectedPrompt?.id,
			},
		};
		handleSaveActionInStore(data);
	};

	return (
		<div className={styles.wrapper}>
			{/* модель генерации */}
			<FormControl fullWidth>
				<Hint hint={"hint_generationModel"} position="left">
					<Autocomplete
						autoHighlight
						options={modelGptList.data}
						value={inputModelGPT}
						onChange={handleModelGPTChange}
						noOptionsText={<NoOptionText />}
						disabled={isDisabled}
						title={inputModelGPT || ""}
						renderInput={(params) => <CustomTextField {...params} label={translate("input_generationModel")} />}
						renderOption={(props, option) => {
							return (
								<span {...props} style={{ fontSize: 13, color: colorPrimary, textAlign: "left" }} key={option}>
									{option}
								</span>
							);
						}}
					/>
				</Hint>
			</FormControl>

			{/* глубина */}
			{selectService === "qas-chat" && (
				<FormControl sx={{ minWidth: "70px" }}>
					<Hint hint={"hint_actionEventDepth"} position="right">
						<CustomTextField
							label={translate("input_depth")}
							type="Number"
							value={inputDepth}
							onChange={(e) => setInputDepth(+e.target.value)}
							error={inputDepth < 1 || inputDepth > 10}
							disabled={isDisabled}
							InputProps={{
								inputProps: { min: 1, max: 10 },
							}}
						/>
					</Hint>
				</FormControl>
			)}

			{/* кэш ответов */}
			{selectService === "qas-ask" && (
				<FormControl fullWidth>
					<Hint hint={"hint_useAnswersCache"} width="100%" position="left">
						<CustomTextField select value={selectCaching} onChange={handleCachingChange} disabled={isDisabled} title={translate("select_useAnswersCache")}>
							{ANSWER_CACHE_USAGE_LIST.map(({ type, translation }) => (
								<CustomMenuItem key={type} value={type}>
									{translate(translation)}
								</CustomMenuItem>
							))}
						</CustomTextField>
					</Hint>
				</FormControl>
			)}

			{/* шаблон запроса */}
			<FormControl fullWidth>
				<Hint hint={"hint_requestTemplate"} position="top">
					<Autocomplete
						autoHighlight
						options={promptList.data?.filter((promptItem) => (selectService === "qas-ask" ? promptItem.type === "ask" : promptItem.type === "chat")) || []}
						value={inputSelectedPrompt}
						onChange={handleSelectedPromptChange}
						noOptionsText={<NoOptionText />}
						disabled={isDisabled}
						title={inputSelectedPrompt?.name}
						renderInput={(params) => <CustomTextField {...params} label={translate("input_requestTemplate")} />}
						getOptionLabel={(option) => option.name}
						renderOption={(props, option) => {
							return (
								<span {...props} style={{ fontSize: 13, color: colorPrimary, textAlign: "left" }} key={option.id}>
									{option.name}
								</span>
							);
						}}
					/>
				</Hint>
			</FormControl>
			{/* кнопка информации о шаблоне */}
			<RequestTemplatePopupInfo inputSelectedPrompt={inputSelectedPrompt} />
		</div>
	);
};

export default SayInternal;
